import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { Alert, Box, Button, IconButton } from "@mui/material";
import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";

const DrawSignature = ({
  onSave,
}: {
  onSave: (data: { type: string; dataURL: string }) => void;
}) => {
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    setError(null); // Clear error on reset
  };

  const validateSignature = () => {
    if (signaturePadRef.current) {
      const trimmedCanvas = signaturePadRef.current.getTrimmedCanvas();
      const ctx = trimmedCanvas.getContext("2d");
      if (!ctx) return false;
      const imageData = ctx.getImageData(
        0,
        0,
        trimmedCanvas.width,
        trimmedCanvas.height,
      );

      // 1. Check total ink area
      let totalInkPixels = 0;
      for (let i = 0; i < imageData.data.length; i += 4) {
        if (imageData.data[i + 3] !== 0) {
          // Alpha channel
          totalInkPixels++;
        }
      }

      const minInkArea = 600; // Minimum number of ink pixels
      if (totalInkPixels < minInkArea) return false;
      return true;
    } else return false;
  };

  const handleAdoptSignature = () => {
    if (signaturePadRef.current) {
      if (!signaturePadRef.current.isEmpty()) {
        const isValidSignature = validateSignature();
        if (isValidSignature) {
          const adoptedSignature = signaturePadRef.current
            .getTrimmedCanvas()
            .toDataURL();

          onSave({ dataURL: adoptedSignature, type: "drawn" });
          handleClearSignature();
        } else {
          setError(
            "Please provide a valid signature. Ensure it is not just dots or tiny strokes.",
          );
        }
      } else {
        setError("Please provide a signature before adopting it.");
      }
    }
  };

  return (
    <>
      {/* Error Message */}
      {error && (
        <Alert
          icon={false}
          severity="error"
          sx={{ mb: 2, justifySelf: "center" }}
        >
          {error}
        </Alert>
      )}
      <Box
        sx={{
          width: { xs: "100%", sm: "80%" },
          mb: 2,
          justifySelf: "center",
          border: "1px solid #ccc",
          borderRadius: "4px",
          overflow: "hidden",
          position: "relative",
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconButton
          size="small"
          onClick={handleClearSignature}
          sx={{
            top: 0,
            right: 0,
            margin: "4px",
            background: "white",
            "&:hover": { background: "white" },
            minWidth: "fit-content",
            textTransform: "none",
            position: "absolute",
            "& .MuiButton-icon": {
              margin: 0,
            },
          }}
        >
          <RotateLeftIcon />
        </IconButton>
        <SignatureCanvas
          ref={signaturePadRef}
          penColor="black"
          throttle={7}
          canvasProps={{
            style: {
              width: "100%",
              height: "100%",
              display: "block",
              backgroundColor: "white",
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 1 }}>
        <Button
          variant="contained"
          onClick={handleAdoptSignature}
          size="medium"
          sx={{ textTransform: "none" }}
        >
          Sign and Continue
        </Button>
      </Box>
    </>
  );
};

export default DrawSignature;
